import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Lock, Truck, CreditCard, Banknote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useCart } from '../context/CartContext';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [errors, setErrors] = useState({});

  const subtotal = getCartTotal();
  const shipping = subtotal >= 50 ? 0 : 5.99;
  const total = subtotal + shipping;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Generate order number
    const orderNumber = 'CLX' + Date.now().toString().slice(-8);
    
    // Store order details for confirmation page
    const orderDetails = {
      orderNumber,
      items: cartItems,
      subtotal,
      shipping,
      total,
      shippingAddress: formData,
      paymentMethod,
      orderDate: new Date().toISOString(),
    };
    
    localStorage.setItem('cartlixs-last-order', JSON.stringify(orderDetails));
    clearCart();
    navigate('/order-confirmation');
  };

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <h1 
          className="text-3xl md:text-4xl font-bold text-slate-800 mb-8"
          style={{ fontFamily: 'Fredoka, sans-serif' }}
        >
          Checkout
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Shipping Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Information */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 
                  className="text-xl font-bold text-slate-800 mb-6"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  Contact Information
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`mt-1 rounded-xl ${errors.firstName ? 'border-red-500' : ''}`}
                      data-testid="checkout-first-name"
                    />
                    {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`mt-1 rounded-xl ${errors.lastName ? 'border-red-500' : ''}`}
                      data-testid="checkout-last-name"
                    />
                    {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`mt-1 rounded-xl ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="checkout-email"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`mt-1 rounded-xl ${errors.phone ? 'border-red-500' : ''}`}
                      data-testid="checkout-phone"
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 
                  className="text-xl font-bold text-slate-800 mb-6"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  Shipping Address
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className={`mt-1 rounded-xl ${errors.address ? 'border-red-500' : ''}`}
                      data-testid="checkout-address"
                    />
                    {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className={`mt-1 rounded-xl ${errors.city ? 'border-red-500' : ''}`}
                        data-testid="checkout-city"
                      />
                      {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className={`mt-1 rounded-xl ${errors.state ? 'border-red-500' : ''}`}
                        data-testid="checkout-state"
                      />
                      {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                    </div>
                    <div>
                      <Label htmlFor="zipCode">ZIP Code *</Label>
                      <Input
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        className={`mt-1 rounded-xl ${errors.zipCode ? 'border-red-500' : ''}`}
                        data-testid="checkout-zip"
                      />
                      {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 
                  className="text-xl font-bold text-slate-800 mb-6"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  Payment Method
                </h2>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                  <div className="space-y-4">
                    <div 
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        paymentMethod === 'cod' ? 'border-sky-500 bg-sky-50' : 'border-slate-200'
                      }`}
                      onClick={() => setPaymentMethod('cod')}
                    >
                      <RadioGroupItem value="cod" id="cod" data-testid="payment-cod" />
                      <Banknote className="text-green-500" size={24} />
                      <div className="flex-1">
                        <Label htmlFor="cod" className="text-base font-semibold cursor-pointer">
                          Cash on Delivery (COD)
                        </Label>
                        <p className="text-sm text-slate-500">Pay when your order arrives</p>
                      </div>
                    </div>
                    
                    <div 
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        paymentMethod === 'card' ? 'border-sky-500 bg-sky-50' : 'border-slate-200'
                      }`}
                      onClick={() => setPaymentMethod('card')}
                    >
                      <RadioGroupItem value="card" id="card" data-testid="payment-card" />
                      <CreditCard className="text-sky-500" size={24} />
                      <div className="flex-1">
                        <Label htmlFor="card" className="text-base font-semibold cursor-pointer">
                          Credit/Debit Card
                        </Label>
                        <p className="text-sm text-slate-500">Coming soon - Pay securely with your card</p>
                      </div>
                      <span className="bg-orange-100 text-orange-600 text-xs font-bold px-2 py-1 rounded-full">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
                <h2 
                  className="text-xl font-bold text-slate-800 mb-6"
                  style={{ fontFamily: 'Fredoka, sans-serif' }}
                >
                  Order Summary
                </h2>

                {/* Items */}
                <div className="space-y-4 mb-6 max-h-60 overflow-y-auto">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex gap-3">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 line-clamp-1">{item.name}</p>
                        <p className="text-sm text-slate-500">Qty: {item.quantity}</p>
                        <p className="text-sm font-bold text-slate-800">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="space-y-3 mb-6 border-t pt-4">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Shipping</span>
                    <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between">
                      <span className="text-lg font-bold text-slate-800">Total</span>
                      <span className="text-lg font-bold text-slate-800" data-testid="checkout-total">
                        ${total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-full py-6 bg-orange-400 hover:bg-orange-500 text-white font-bold text-lg shadow-[0px_4px_0px_0px_#cc8400] hover:shadow-[0px_2px_0px_0px_#cc8400] active:shadow-none transition-all"
                  data-testid="place-order-btn"
                >
                  Place Order
                </Button>

                {/* Trust */}
                <div className="mt-6 space-y-2">
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <Lock className="text-green-500" size={16} />
                    <span>Secure 256-bit SSL encryption</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <ShieldCheck className="text-sky-500" size={16} />
                    <span>Your data is protected</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
